import { ChangeDetectionStrategy, Component, EventEmitter, Inject, InjectionToken, Injector, Input, Optional, Output, Provider } from "@angular/core";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { EMPTY, from, Observable, ObservableInput, of, ReplaySubject } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { CommonIntegratedArchitectureFrameworkAuthService } from "../common-integrated-architecture-framework-auth.service";

export const ROLE_PAGE_ROUTE_PROVIDER = new InjectionToken("ROLE_PAGE_ROUTE_PROVIDER");

export function provideRolePageRoute(route: IAdaptRoute<{ roleId: number }>): Provider {
    return {
        provide: ROLE_PAGE_ROUTE_PROVIDER,
        useValue: route,
        multi: false,
    };
}

type ConfigureRoleAction = (injector: Injector, role: Role) => ObservableInput<any>;

export const CONFIGURE_ROLE_ACTION = new InjectionToken<ConfigureRoleAction>("CONFIGURE_ROLE_ACTION");

export function provideConfigureRoleAction(action: ConfigureRoleAction): Provider {
    return {
        provide: CONFIGURE_ROLE_ACTION,
        useValue: action,
        multi: false,
    };
}


@Component({
    selector: "adapt-link-role",
    templateUrl: "./link-role.component.html",
    styleUrls: ["./link-role.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class LinkRoleComponent extends BaseComponent {
    @Input() public showIcon = false;
    @Input() public set role(value: Role | undefined) {
        this.role$.next(value);
    }

    @Input() public openConfigOnClick = false;
    @Output() public roleConfigured = new EventEmitter<Role>();

    public role$ = new ReplaySubject<Role | undefined>(1);
    public roleDescriptionUrl$: Observable<string | null>;

    public canConfigureRole = false;

    public constructor(
        commonIntegratedArchAuthService: CommonIntegratedArchitectureFrameworkAuthService,
        private injector: Injector,
        @Optional() @Inject(ROLE_PAGE_ROUTE_PROVIDER) roleDescriptionRoute?: IAdaptRoute<{ roleId: number }>,
        @Optional() @Inject(CONFIGURE_ROLE_ACTION) private configureRoleAction?: ConfigureRoleAction,
    ) {
        super();

        this.role$.pipe(
            switchMap((role) => role
                ? commonIntegratedArchAuthService.canEditRoleContents(role)
                : of(false)),
            this.takeUntilDestroyed(),
        ).subscribe((canConfigureRole) => this.canConfigureRole = canConfigureRole);

        this.roleDescriptionUrl$ = roleDescriptionRoute
            ? this.role$.pipe(
                switchMap((role) => role && role.roleId > 0
                    ? roleDescriptionRoute.getRoute(role)
                    : of(null)),
            )
            : of(null);
    }

    public configureRole(role: Role) {
        return from(this.configureRoleAction?.(this.injector, role) ?? EMPTY).pipe(
            tap(() => this.roleConfigured.emit(role)),
            this.takeUntilDestroyed(),
        );
    }
}
